//* External
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';

//* App Custom
import { Button, CustomIcon } from '@componentsApp/common/index';
import { tnl } from '@i18nApp/i18n';
import InputLabel from '../InputLabel/InputLabel';
import './inputDropzone.scss';

const InputDropzone = ({
  name,
  label,
  labelHelper,
  extensions = [],
  disabled = false,
  required = false,
  design = 'col-12',
  hookError,
  isLoading = false,
  defaultValue,
  filePreSelected = null,
  hookOnChange = () => {},
  onInputChange = () => {}
}) => {
  const [fileSelected, setFileSelected] = useState(filePreSelected);
  const acceptedExtensions = extensions
    .map((extension) => `.${extension}`)
    .join(', ');

  const fileSize = useMemo(() => {
    if (fileSelected) {
      return Number((fileSelected.size / (1024 * 1024)).toFixed(2));
    } else return null;
  }, [fileSelected]);

  return (
    <div className={design}>
      <div className={`customInput ${disabled ? 'customInput_disabled' : ''}`}>
        <InputLabel
          name={name}
          text={label}
          helper={labelHelper}
          required={required}
        />
        <Dropzone
          disabled={disabled}
          accept={acceptedExtensions}
          onDrop={(files) => {
            setFileSelected(files[0]);
            hookOnChange(files[0]);
            onInputChange(files[0]);
          }}
          noDrag
        >
          {({ getRootProps, getInputProps }) => (
            <div
              className={`inputDropzone d-flex gap-2 ${
                disabled ? 'inputDropzone_disabled' : ''
              }`}
            >
              <div
                className="inputDropzone_trigger items_row_centered"
                {...getRootProps()}
              >
                {isLoading ? (
                  <Spinner
                    role="loading_spinner"
                    animation="border"
                    className="dropzone_spinner"
                  />
                ) : (
                  <CustomIcon name="fileArrowUp" size="MD" />
                )}
                <input {...getInputProps()} hidden />
              </div>
              <div className="d-flex flex-column justify-content-center">
                {!fileSelected ? (
                  <div>
                    {tnl('components.input_dropzone_extensions')}
                    <span className="ps-1 fw-bold">{acceptedExtensions}</span>
                  </div>
                ) : (
                  tnl('components.input_dropzone_file_loaded')
                )}

                {fileSelected && (
                  <div className="inputDropzone_thumb d-flex gap-2 align-items-center">
                    <p>
                      {fileSelected?.path} - {fileSize}MB
                    </p>
                    <button
                      type="button"
                      disabled={disabled}
                      className="inputDropzone_thumb_btn"
                      onClick={() => {
                        setFileSelected(null);
                        hookOnChange(null);
                        onInputChange(null);
                      }}
                    >
                      X
                    </button>
                  </div>
                )}
                <div className="d-flex gap-1 align-items-center">
                  <Button
                    variant="link"
                    iconName="fileDownload"
                    fileToDownload={defaultValue}
                    text="buttons.download_file"
                  />
                  <button
                    type="button"
                    disabled={disabled}
                    className="inputDropzone_thumb_btn"
                    onClick={() => {
                      setFileSelected(null);
                      hookOnChange(null);
                      onInputChange(null);
                      toast.success(tnl('toasts.file_deleted'));
                    }}
                  >
                    X
                  </button>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
        {hookError?.message && (
          <p className="customInput_error">{tnl(hookError?.message)}</p>
        )}
      </div>
    </div>
  );
};

InputDropzone.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  extensions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  design: PropTypes.string,
  hookError: PropTypes.shape({
    message: PropTypes.string
  }),
  onInputChange: PropTypes.func,
  hookOnChange: PropTypes.func,
  filePreSelected: PropTypes.object,
  isLoading: PropTypes.bool,
  defaultValue: PropTypes.string
};

export default InputDropzone;
